import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Option } from 'slim-select/dist/data.d';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import { RootState } from '../../../../core/state/state';
import Select from '../../../../components/form/select/Select';
import { ExternalUser, UserAccountOption } from '../../../../core/state/users/types';
import Radio from '../../../../components/form/radio/Radio';
import { changeUserAccountStatus } from '../../../../core/state/subcontractors/asyncActions';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { getExternalUsers } from '../../../../core/state/users/asyncActions';
import { Color } from '../../../../core/enums/Color';

import './EditUserStatusModal.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
}

type EditUserStatusFormData = {
  user: string;
  status: UserAccountOption;
};

const EditUserStatusModal: React.FC<Props> = ({ isOpen, closeModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const users = useSelector((state: RootState) => state.users.externalUsers);

  const usersToOption = (user: ExternalUser): Option => ({
    value: user.id.toString(),
    text: user.name
  });

  const userOptions = useMemo(() => users?.map(usersToOption), [users]);

  const validationSchema = yup
    .object()
    .shape({
      user: yup.string().required(),
      status: yup.string().required()
    })
    .required();

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isValid }
  } = useForm<EditUserStatusFormData>({
    mode: 'onChange',
    defaultValues: {
      user: ''
    },
    resolver: yupResolver(validationSchema)
  });
  const userAccountOptions = useMemo(
    () => [
      {
        label: t('externalUsers.editStatus.form.option.unblock'),
        value: t('externalUsers.editStatus.form.option.unblock')
      },
      {
        label: t('common.block'),
        value: t('common.block')
      }
    ],
    [t]
  );

  const onSubmit = async (formData: EditUserStatusFormData) => {
    const selectedUser = users?.find((u) => u.id === Number(formData.user));
    if (selectedUser) {
      await dispatch(changeUserAccountStatus(selectedUser.id, formData.status !== UserAccountOption.Block));
      await dispatch(getExternalUsers());
      dispatch(
        spawnGlobalNotification({
          type: GlobalNotificationTypes.Success,
          message: t('externalUsers.editStatus.message.success')
        })
      );
    }
    closeModal();
  };

  const title = <h3 className="modal-title">{t('externalUsers.editStatus.modal.title')}</h3>;
  const buttons = (
    <div className="modal-buttons">
      <Button color={Color.Blue} buttonTypes={['transparent']} onClick={closeModal}>
        <span>{t('common.cancel')}</span>
      </Button>
      <Button disabled={!isValid} form="edit-user-status" type="submit" buttonTypes={['blue']}>
        {t('common.save')}
      </Button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} title={title} toolbar={buttons} size="small" height={285}>
      <form id="edit-user-status" className="edit-user-status-modal" onSubmit={handleSubmit(onSubmit)}>
        <div className="edit-user-status-modal__content">
          <Controller
            name="user"
            control={control}
            render={({ field: { onChange, name } }) => (
              <Select
                id="user"
                label={t('common.companyName')}
                name={name}
                options={userOptions}
                onChange={(option: any) => {
                  const user = users?.find((f) => String(f.id) === option.value);
                  const status: UserAccountOption = user?.active === false ? t('common.block') : t('externalUsers.editStatus.form.option.unblock');
                  setValue('status', status);
                  if (Array.isArray(option)) {
                    [option] = option; // eslint-disable-line no-param-reassign
                  }
                  onChange(option.value);
                }}
                addPlaceholderOption
                required
              />
            )}
          />
          <Controller
            name="status"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <Radio id="status" name={name} label={t('common.status')} options={userAccountOptions} defaultValue={value} onChange={onChange} />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default EditUserStatusModal;
